import React, { useRef, useState } from "react";
import { TESTIMONIALS, BRAND } from "../mock/mock";
import { Star, ChevronLeft, ChevronRight, X, Quote } from "lucide-react";

export default function Testimonials() {
  const scrollRef = useRef(null);
  const [active, setActive] = useState(null); // open review modal

  const scrollByCard = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector("[data-review-card]");
    const step = card ? card.offsetWidth + 24 : Math.round(el.clientWidth * 0.9);
    requestAnimationFrame(() => {
      el.scrollBy({ left: dir * step, behavior: "smooth" });
    });
  };

  return (
    <section id="reviews" className="py-24 md:py-32 bg-viimii-2 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="reveal text-center max-w-2xl mx-auto mb-14">
          <p className="section-line text-gold mb-4">Testimonials</p>
          <h2 className="font-display text-4xl md:text-5xl font-medium leading-tight">
            What our clients <span className="gold-gradient-text italic">say.</span>
          </h2>
          <div className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border hairline">
            <span className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} className="fill-gold" style={{ color: "var(--viimii-gold)" }} />
              ))}
            </span>
            <span className="text-cream/80 text-sm">{BRAND.rating}★ on Google</span>
          </div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        {/* Left / Right controls */}
        <button
          onClick={() => scrollByCard(-1)}
          data-testid="reviews-prev"
          aria-label="Scroll reviews left"
          className="hidden md:grid absolute left-0 top-1/2 -translate-y-1/2 z-10 w-11 h-11 place-items-center rounded-full border hairline bg-viimii text-cream/70 hover:text-gold shadow-lg transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => scrollByCard(1)}
          data-testid="reviews-next"
          aria-label="Scroll reviews right"
          className="hidden md:grid absolute right-0 top-1/2 -translate-y-1/2 z-10 w-11 h-11 place-items-center rounded-full border hairline bg-viimii text-cream/70 hover:text-gold shadow-lg transition-colors"
        >
          <ChevronRight size={20} />
        </button>

        {/* Manually scrollable row (swipe on touch, drag/trackpad, or arrows) */}
        <div
          ref={scrollRef}
          data-testid="reviews-scroll"
          className="flex gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 -mx-5 px-5 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {TESTIMONIALS.map((t) => (
            <button
              key={t.id}
              data-review-card
              data-testid={`review-card-${t.id}`}
              onClick={() => setActive(t)}
              className="w-[340px] shrink-0 snap-start text-left p-7 rounded-[26px] border hairline bg-viimii hover:border-gold transition-colors cursor-pointer group"
            >
              <div className="flex mb-4">
                {[...Array(5)].map((_, s) => (
                  <Star key={`${t.id}-star-${s}`} size={15} className="fill-gold" style={{ color: "var(--viimii-gold)" }} />
                ))}
              </div>
              <p className="text-sm text-cream/70 leading-relaxed min-h-[96px]">“{t.text}”</p>
              <div className="flex items-center gap-3 mt-5 pt-5 border-t hairline">
                <span
                  className="w-10 h-10 rounded-full grid place-items-center font-display text-black"
                  style={{ background: "var(--viimii-clay-soft)" }}
                >
                  {t.name[0]}
                </span>
                <span className="text-sm text-cream font-medium flex-1">{t.name}</span>
                <span className="text-xs text-cream/40 group-hover:text-gold transition-colors">Read →</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="text-center mt-14">
        <a
          href={BRAND.mapUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border hairline text-cream/85 hover:text-gold transition-colors"
        >
          Review us on Google →
        </a>
      </div>

      {/* Full review modal */}
      {active && (
        <div className="fixed inset-0 z-[80] grid place-items-center p-4" data-testid="review-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setActive(null)} />
          <div className="relative w-full max-w-md bg-viimii-2 border hairline rounded-[26px] p-8">
            <button onClick={() => setActive(null)} className="absolute top-4 right-4 text-cream/60 hover:text-cream" aria-label="Close">
              <X size={20} />
            </button>
            <span className="w-11 h-11 rounded-full grid place-items-center mb-4" style={{ background: "rgba(176,134,63,0.15)" }}>
              <Quote size={20} style={{ color: "var(--viimii-gold)" }} />
            </span>
            <div className="flex mb-4">
              {[...Array(5)].map((_, s) => (
                <Star key={s} size={16} className="fill-gold" style={{ color: "var(--viimii-gold)" }} />
              ))}
            </div>
            <p className="text-cream/80 leading-relaxed text-[15px]">“{active.text}”</p>
            <div className="flex items-center gap-3 mt-6 pt-5 border-t hairline">
              <span
                className="w-11 h-11 rounded-full grid place-items-center font-display text-lg text-black"
                style={{ background: "var(--viimii-clay-soft)" }}
              >
                {active.name[0]}
              </span>
              <div>
                <p className="text-cream font-medium">{active.name}</p>
                <p className="text-xs text-cream/50">Google Review</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
